import { TooltipStandard } from 'components/tooltip/TooltipStandard';
import { useGlobalContext } from 'contexts/GlobalContext';
import { useEffect, useMemo, useState } from 'react';
import { getUserToken } from 'services/api/requests/biIntegration';
import { globalStyles } from 'theme/styles';

interface AnalyticsProButtonProps {
	setSidebarHelpState?: (state: string) => void;
}

export const AnalyticsProButton: React.FC<AnalyticsProButtonProps> = () => {
	const context = useGlobalContext();
	const { permissionsPage, pathCompanyName } = context || {};
	const [hasBiToken, setHasBiToken] = useState<boolean | null>(null);
	const [isCheckingToken, setIsCheckingToken] = useState(true);

	const colors = globalStyles.colors.loader360;
	const gradientId = useMemo(
		() =>
			`analytics-pro-gradient-${Math.random()
				.toString(36)
				.substring(2, 11)}`,
		[]
	);

	useEffect(() => {
		const checkBiToken = async () => {
			try {
				setIsCheckingToken(true);
				const token = await getUserToken();
				const hasToken =
					!!token &&
					typeof token === 'string' &&
					token.trim().length > 0;
				setHasBiToken(hasToken);
			} catch (error) {
				// eslint-disable-next-line no-console
				console.debug('Token BI não encontrado:', error);
				setHasBiToken(false);
			} finally {
				setIsCheckingToken(false);
			}
		};

		if (context && permissionsPage) {
			checkBiToken();
		} else {
			setHasBiToken(false);
			setIsCheckingToken(false);
		}
	}, [context, permissionsPage]);

	if (!context || !permissionsPage || isCheckingToken) {
		return null;
	}

	const hasBiPermission = hasBiToken === true;

	const handleClick = (e: React.MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();
		if (!hasBiPermission || !pathCompanyName) {
			return;
		}
		const url = `/${pathCompanyName}/analytics-pro`;
		window.open(url, '_blank', 'noopener,noreferrer');
	};

	const tooltipMessage = hasBiPermission
		? 'Acessar Analytics Pro - Portal completo de relatórios avançados'
		: 'Analytics Pro não faz parte do seu pacote atual. Contate a mentoria e descubra como ter acesso a relatórios avançados.';

	return (
		<TooltipStandard
			label={tooltipMessage}
			placement='top'
		>
			<span
				onClick={handleClick}
				onKeyDown={e => {
					if (
						hasBiPermission &&
						(e.key === 'Enter' || e.key === ' ')
					) {
						e.preventDefault();
						handleClick(e as any);
					}
				}}
				role={hasBiPermission ? 'button' : undefined}
				tabIndex={hasBiPermission ? 0 : -1}
				style={{
					marginRight: '10px',
					padding: '6px 12px',
					borderRadius: '6px',
					opacity: 1,
					cursor: hasBiPermission ? 'pointer' : 'not-allowed',
					display: 'inline-flex',
					alignItems: 'center',
					gap: '8px',
					transition: 'all 0.2s',
					userSelect: 'none',
					background: 'transparent',
					outline: 'none',
					border: 'none',
					boxShadow: 'none',
				}}
				onFocus={e => {
					e.currentTarget.style.outline = 'none';
					e.currentTarget.style.border = 'none';
				}}
				onBlur={e => {
					e.currentTarget.style.outline = 'none';
					e.currentTarget.style.border = 'none';
				}}
				onMouseEnter={e => {
					if (hasBiPermission) {
						e.currentTarget.style.outline = 'none';
						e.currentTarget.style.border = 'none';
						e.currentTarget.style.transform =
							'translateY(-2px) scale(1.1)';
						e.currentTarget.style.opacity = '0.9';
					}
				}}
				onMouseLeave={e => {
					if (hasBiPermission) {
						e.currentTarget.style.outline = 'none';
						e.currentTarget.style.border = 'none';
						e.currentTarget.style.transform =
							'translateY(0) scale(1)';
						e.currentTarget.style.boxShadow = 'none';
						e.currentTarget.style.opacity = '1';
					}
				}}
				onMouseDown={e => {
					if (hasBiPermission) {
						e.currentTarget.style.outline = 'none';
						e.currentTarget.style.border = 'none';
						e.currentTarget.style.transform =
							'translateY(0) scale(0.95)';
						e.currentTarget.style.opacity = '0.8';
					}
				}}
				onMouseUp={e => {
					if (hasBiPermission) {
						e.currentTarget.style.outline = 'none';
						e.currentTarget.style.border = 'none';
						e.currentTarget.style.transform =
							'translateY(-2px) scale(1.1)';
						e.currentTarget.style.opacity = '0.9';
					}
				}}
			>
				<svg
					xmlns='http://www.w3.org/2000/svg'
					height='36'
					width='36'
					viewBox='-4.83 -8.439875 41.86 50.63925'
					style={{
						display: 'block',
						outline: 'none',
						border: 'none',
					}}
				>
					<defs>
						<linearGradient
							id={gradientId}
							gradientUnits='userSpaceOnUse'
							x1='0%'
							y1='0%'
							x2='100%'
							y2='100%'
						>
							<stop
								offset='0%'
								stopColor={colors.gradientStart}
								className='gradient-stop-1'
							>
								<animate
									attributeName='stop-color'
									values={`${colors.gradientStart};${colors.gradientEnd};${colors.gradientStart}`}
									dur='3s'
									repeatCount='indefinite'
								/>
							</stop>
							<stop
								offset='100%'
								stopColor={colors.gradientEnd}
								className='gradient-stop-2'
							>
								<animate
									attributeName='stop-color'
									values={`${colors.gradientEnd};${colors.gradientStart};${colors.gradientEnd}`}
									dur='3s'
									repeatCount='indefinite'
								/>
							</stop>
						</linearGradient>
					</defs>
					<g transform='translate(-.9 -.1405)'>
						<defs>
							<path
								id={`${gradientId}-clip`}
								d='M.9 0h32.2v33.9H.9z'
							/>
						</defs>
						<clipPath id={`${gradientId}-clipPath`}>
							<use
								height='100%'
								width='100%'
								overflow='visible'
								href={`#${gradientId}-clip`}
							/>
						</clipPath>
						<path
							d='M11.1 27.3c0 1.1-.9 2.1-2.1 2.1-1.2 0-2.1-.9-2.1-2.1v-9c0-1.1.9-2.1 2.1-2.1 1.2 0 2.1.9 2.1 2.1zm6 2.4c0 1.1-.9 2.1-2.1 2.1-1.2 0-2-.9-2-2.1V16c0-1.1.9-2.1 2.1-2.1 1.2 0 2.1.9 2.1 2.1v13.7zM5 25.3c0 1.1-.9 2.1-2.1 2.1-1.2 0-2.1-.9-2.1-2.1v-4.9c0-1.1.9-2.1 2.1-2.1 1.2 0 2.1 1 2.1 2.1zm18.1 6.6c0 1.1-.9 2.1-2.1 2.1-1.1 0-2-1-2-2.1V13.7c0-1.1.9-2.1 2.1-2.1 1.1 0 2.1.9 2.1 2.1v18.2z'
							fill={`url(#${gradientId})`}
							clipPath={`url(#${gradientId}-clipPath)`}
						/>
						<path
							d='M29.7 31c-.4 0-.7-.1-1.1-.2l-2.5-.8c-.4-.1-.7-.6-.5-1 .1-.4.6-.7 1-.5l2.6.8c.5.2 1.1.1 1.6-.3.5-.3.7-.9.7-1.4V10.9c0-.8-.5-1.5-1.2-1.7L5.8 1.8c-.5-.2-1.1-.1-1.6.2-.4.4-.7.9-.7 1.5V15c0 .5-.4.8-.8.8-.5 0-.8-.4-.8-.8V3.5c0-1.1.5-2.1 1.4-2.7.9-.7 2-.8 3-.5l24.4 7.4c1.4.4 2.4 1.8 2.4 3.3v16.7c0 1.1-.5 2.1-1.4 2.8-.6.2-1.3.5-2 .5'
							fill={`url(#${gradientId})`}
							clipPath={`url(#${gradientId}-clipPath)`}
						/>
					</g>
				</svg>
				<span
					style={{
						fontSize: '14px',
						fontWeight: 600,
						color: 'inherit',
						whiteSpace: 'nowrap',
					}}
				>
					Analytics Pro
				</span>
			</span>
		</TooltipStandard>
	);
};
